<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import CheckboxToggle from "@/components/form/CheckboxToggle.vue"
import { useCurrentVarsityApp, useVarsityAppConfig } from "@/composables"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import { useIssueOfficeParticipants } from "@/composables/useIssueOfficeParticipants"
import type { ChampionshipRegistrationCustomTask } from "@/model/championship/championship-registration-custom-task"
import type { IssueState } from "@/model/tasks/issue-state"
import { type Task } from "@/model/tasks/task"
import IssueRespond from "@/pages/Tasks/Issue/IssueRespond.vue"
import ChampionshipRegistrationTask from "@/pages/Tasks/Tasks/ChampionshipRegistration/ChampionshipRegistrationTask.vue"
import RefundFailed from "@/pages/Tasks/Tasks/RefundFailed.vue"
import ShopOrderApproval from "@/pages/Tasks/Tasks/ShopOrderApproval.vue"
import ShopOrderConfirmation from "@/pages/Tasks/Tasks/ShopOrderConfirmation.vue"
import ShopPayOrder from "@/pages/Tasks/Tasks/ShopPayOrder.vue"
import ShopSelectProducts from "@/pages/Tasks/Tasks/ShopSelectProducts.vue"
import JobActivityInvitation from "@/pages/Tasks/Tasks/Staff/JobActivityInvitation.vue"
import SubmitPreparedOrganizationMember from "@/pages/Tasks/Tasks/SubmitPreparedOrganizationMember.vue"
import { useAppState } from "@/stores"
import { useGlobalState } from "@/stores/useGlobalState"
import { defineTranslations } from "@/utils/defineTranslations"
import { $date, $datetime, type ListState, useHttpClient, useInlineTable, useLoadEntity } from "@/vf"
import AnimateIf from "@/vf/components/AnimateIf.vue"
import { computed, inject, provide, reactive, ref } from "vue"
import AcceptOrganizationRequest from "../Tasks/AcceptOrganizationRequest.vue"
import AccountInvitation from "../Tasks/AccountInvitation.vue"
import ApproveAddressUpdate from "../Tasks/ApproveAddressUpdate.vue"
import ApproveOrganizationDebitorProfile from "../Tasks/ApproveOrganizationDebitorProfile.vue"
import AuditPersonUpload from "../Tasks/AuditPersonUpload.vue"
import ChampionshipRegistrationAcceptBid from "../Tasks/ChampionshipRegistrationAcceptBid.vue"
import ChampionshipRegistrationAcceptEventInfo from "../Tasks/ChampionshipRegistrationAcceptEventInfo.vue"
import ChampionshipRegistrationApprove from "../Tasks/ChampionshipRegistrationApprove.vue"
import ChampionshipRegistrationCustom from "../Tasks/ChampionshipRegistrationCustom.vue"
import ChampionshipRegistrationFinalDocuments from "../Tasks/ChampionshipRegistrationFinalDocuments.vue"
import ChampionshipRegistrationFinalizeTeams from "../Tasks/ChampionshipRegistrationFinalizeTeams.vue"
import ChampionshipRegistrationPayOrder from "../Tasks/ChampionshipRegistrationPayOrder.vue"
import ChampionshipRegistrationSelectTeam from "../Tasks/ChampionshipRegistrationSelectTeam.vue"
import ChampionshipRegistrationTeamCancellation from "../Tasks/ChampionshipRegistrationTeamCancellation.vue"
import ChampionshipRegistrationWaitingList from "../Tasks/ChampionshipRegistrationWaitingList.vue"
import CommunicationMessage from "../Tasks/CommunicationMessage.vue"
import ApproveLoginJobActivity from "../Tasks/Staff/ApproveLoginJobActivity.vue"
import StaffReviewContract from "../Tasks/Staff/StaffReviewContract.vue"
import StaffReviewGeneralTerms from "../Tasks/Staff/StaffReviewGeneralTerms.vue"
import StaffSignContract from "../Tasks/Staff/StaffSignContract.vue"
import TaskAttachmentList from "../Tasks/TaskAttachmentList.vue"
import IssueSettings from "./IssueSettings.vue"
import { createModuleConfiguration, type IssueListItem } from "./config"
import { TaskViewMode } from "./util"

const props = withDefaults(
    defineProps<{
        id: string
        viewMode: "admin" | "customer"
        filterOnlyMessages?: boolean
        onTaskRead?: () => void
        backButton?: boolean
    }>(),
    {
        viewMode: "customer",
        filterOnlyMessages: false,
        onTaskRead: undefined,
        backButton: true,
    },
)

const http = useHttpClient()

const inlineTable = useInlineTable(false)
const [config] = createModuleConfiguration()
const { item: issue, refresh } = await useLoadEntity<IssueListItem>(props.id, config)
const { refreshGlobalState } = useGlobalState()
refreshGlobalState() // refresh state to update the unread messages count in the menu
const { currentVarsityApp } = useCurrentVarsityApp()
const listState = inject<ListState>("list-state")
const { appConfig } = useVarsityAppConfig()
const { loadOfficeParticipants } = useIssueOfficeParticipants()

const openReply = ref(false)
const openSettings = ref(false)
const isAdmin = computed(() => props.viewMode === "admin")

const appState = useAppState()

const tasksLogged: string[] = []
const showAccessLogs: Record<string, boolean> = reactive({})

provide(TaskViewMode, props.viewMode)

const trans = defineTranslations({
    "task_access_logs.title": {
        de: "Task Access Logs",
        en: "Task Access Logs",
    },
    "task_access_logs.no_results": {
        de: "Keine Zugriffslogs vorhanden.",
        en: "No access logs available.",
    },
    "task_access_logs.table.date": {
        de: "Datum",
        en: "Date",
    },
    "task_access_logs.table.user": {
        de: "Benutzer",
        en: "User",
    },
    "task_access_logs.table.app": {
        de: "App",
        en: "App",
    },
    "hide_issue": {
        de: "Verbergen",
        en: "Hide",
    },
    "unhide_issue": {
        de: "Einblenden",
        en: "Show",
    },
    "join_issue": {
        de: "Mir zuweisen",
        en: "Assign to me",
    },
})

async function updated() {
    await inlineTable.emit("refresh")
    await refresh()

    console.log("updated: ", issue.value.id, issue.value.state)

    if (issue.value.state == "done") {
        inlineTable.close()
    }

    for (const task of issue.value.tasks ?? []) {
        if (task.state == "future") {
            // close all future tasks if we've gone back in the issues progress (e.g., championship registration)
            expandedTasks[task.id!] = false
        }
    }
}

async function toggleHidden() {
    const newHidden = !issue.value.hidden
    await http.post(`tasks/issue/${issue.value.id}/hidden`, { hidden: newHidden })
    issue.value.hidden = newHidden
}

const isCurrentUserParticipant = computed(() => {
    const staffAccountId = appConfig.value.staffAccount?.id
    if (!staffAccountId) return true // Hide button if no staff account

    return issue.value.participants?.some(p => p.accountId === staffAccountId) ?? false
})

const canJoinIssue = computed(() => {
    return props.viewMode === "admin" && appConfig.value.staffAccount && !isCurrentUserParticipant.value
})

async function joinIssue() {
    await http.post(`tasks/issue/${issue.value.id}/add-participant`, {
        accountId: appConfig.value.staffAccount?.id,
    })
    await refresh()

    // Update the list item's office participants without reloading the full list
    const listItem = listState?.list?.find(i => i.id === issue.value.id) as IssueListItem | undefined
    if (listItem) {
        await loadOfficeParticipants([listItem])
    }
}

async function toggleState(task: Task) {
    if (!canToggleTask(task)) {
        return
    }

    if (["pending", "canceled", "future"].includes(task.state)) {
        return
    }

    const state = task.state == "open" ? "closed" : "open"
    const newIssueState = (
        await http.post<{
            issueState: IssueState
        }>(`tasks/task/${task.id}/state`, { state })
    ).data.issueState
    if (newIssueState != issue.value.state) {
        issue.value.state = newIssueState
        await updated()
    }
    task.state = state
}

const expandedTasks = reactive({}) as { [id: string]: boolean }
const filterOnlyMessages = ref(props.filterOnlyMessages)

function taskIsExpanded(task: Task) {
    const expanded =
        (!["closed", "canceled", "rejected", "future"].includes(task.state) && expandedTasks[task.id!] === undefined) ||
        expandedTasks[task.id!]
    if (expanded) {
        logTaskAccess(task)
    }
    return expanded
}

function canExpandTask(task: Task) {
    return task.state !== "future"
}

function customTaskTitle(task: Task): string | undefined {
    if (task.taskType !== "ChampionshipRegistrationCustom") {
        return undefined
    }

    // custom tasks carry the admin-configured, already translated title of their definition
    return (task as ChampionshipRegistrationCustomTask).definition?.title
}

function expandTask(task: Task) {
    if (!canExpandTask(task)) {
        return
    }

    expandedTasks[task.id!] = !taskIsExpanded(task)
}

function taskCanBeOpened(task: Task) {
    return (isAdmin.value && task.canBeManuallyOpenedByAdmin) || (!isAdmin.value && task.canBeManuallyOpenedByUser)
}

function taskCanBeClosed(task: Task) {
    return (isAdmin.value && task.canBeManuallyClosedByAdmin) || (!isAdmin.value && task.canBeManuallyClosedByUser)
}

function canToggleTask(task: Task) {
    if (task.state == "closed") {
        return taskCanBeOpened(task)
    }
    if (task.state == "open") {
        return taskCanBeClosed(task)
    }
    return false
}

function canAnyTaskBeClosed() {
    return Array.isArray(issue.value.tasks) && issue.value.tasks.some(task => taskCanBeClosed(task))
}

function allTasksCanBeClosed() {
    return (
        Array.isArray(issue.value.tasks) &&
        issue.value.tasks.every(task => taskCanBeClosed(task) || task.state == "closed")
    )
}

const inlineCloseAll = createInlineConfirm({
    async action() {
        await http.post(`tasks/task/close-all/${issue.value.id}`)
    },
    afterAction() {
        updated()
    },
})

function logTaskAccess(task: Task) {
    if (
        !task.id ||
        tasksLogged.includes(task.id) ||
        (appConfig.value.isSuperAdmin && currentVarsityApp.value !== "MyVarsity")
    ) {
        return
    }
    tasksLogged.push(task.id)
    http.post(`tasks/task/${task.id}/${currentVarsityApp.value}/access`).then(() => {
        const listItem = listState?.list?.find(i => i.id === issue.value.id)
        if (listItem && listItem.unreadMessageIds) {
            const index = listItem.unreadMessageIds.indexOf(task.id)
            if (index > -1) {
                listItem.unreadMessageIds.splice(index, 1)
            }
        }
    })
}

function toggleTaskAccessLogs(taskId: string) {
    showAccessLogs[taskId] = !showAccessLogs[taskId]
}
</script>

<template>
    <div class="open-row-container-flush bg-white">
        <div class="bg-primary align-items-center text-white py-2 pr-4 d-none d-sm-flex">
            <div @click="inlineTable.close()" class="c-pointer py-3 pr-3 pl-4" v-if="backButton">
                <i class="fa fa-fw fa-chevron-left"></i>
                <span>{{ $t("crud:button.back") }}</span>
            </div>

            <div class="pr-3" v-if="backButton">
                <i class="fa fa-fw fa-ellipsis-vertical"></i>
            </div>

            <div class="flex-grow-1" :class="{ 'ml-4': !backButton }">
                <template v-for="issueGroup in issue.issueGroupTree" :key="issueGroup.id">
                    {{ issueGroup.name }}
                    <i class="fa fa-fw fa-chevron-right"></i>
                </template>
                <template v-for="issueGroup in issue.productGroupTree" :key="issueGroup.id">
                    {{ issueGroup.name }}
                    <i class="fa fa-fw fa-chevron-right"></i>
                </template>

                {{ $t(issue.name!, issue.nameProps) }}
            </div>

            <CheckboxToggle v-model="filterOnlyMessages">
                {{ $t("@tasks:issue.only_messages") }}
            </CheckboxToggle>

            <div
                class="c-pointer p-3 fs-1-5"
                @click="openSettings = !openSettings"
                v-if="viewMode === 'admin'"
                :class="{ 'text-primary-dark': openSettings }"
            >
                <i class="fa fa-fw fa-cog"></i>
            </div>

            <div class="c-pointer p-3 fs-1-5" @click="joinIssue" v-if="canJoinIssue" :title="trans('join_issue')">
                <i class="fa fa-fw fa-user-plus"></i>
            </div>

            <div
                class="c-pointer p-3 fs-1-5"
                @click="toggleHidden"
                v-if="viewMode === 'admin'"
                :title="issue.hidden ? trans('unhide_issue') : trans('hide_issue')"
            >
                <i class="fa fa-fw" :class="issue.hidden ? 'fa-eye-slash' : 'fa-eye'"></i>
            </div>
        </div>

        <AnimateIf :if="openSettings">
            <IssueSettings :issue="issue" @updated="updated" class="border-bottom border-primary px-4 py-3" />
        </AnimateIf>

        <div
            v-for="(task, index) of issue.tasks"
            :key="task.id"
            :class="{ 'bg-light': index % 2 === 0 }"
            class="border-bottom border-primary"
        >
            <AnimateIf :if="!filterOnlyMessages || task.taskType === 'CommunicationMessage'">
                <div
                    class="py-4 px-3 p-sm-4 d-sm-flex align-items-center"
                    :class="task.state === 'future' ? 'bg-future' : 'bg-white'"
                >
                    <span class="p-1 p-sm-2 mr-3 c-pointer" @click="expandTask(task)" style="width: 24px">
                        <i
                            class="fa-solid fa-chevron-right transition-transform"
                            :class="{ 'fa-rotate-90': taskIsExpanded(task) }"
                            v-if="canExpandTask(task)"
                        ></i>
                    </span>
                    <div
                        class="border text-center d-inline-block d-sm-block mr-3 mr-sm-0 bg-white"
                        style="width: 20px; height: 20px"
                        :class="{
                            'c-pointer': canToggleTask(task),
                        }"
                        @click="toggleState(task)"
                        :title="
                            task.closedInfo
                                ? task.closedInfo.login?.name + ', ' + $datetime(task.closedInfo.createdAt)
                                : undefined
                        "
                    >
                        <i class="fa-solid fa-check" v-if="task.state === 'closed'"></i>
                        <i class="fa-solid fa-times" v-if="task.state === 'canceled' || task.state === 'rejected'"></i>
                        <i class="fa-solid fa-hourglass" v-if="task.state === 'pending'"></i>
                    </div>
                    <div style="width: 15ex; flex-basis: 15ex; flex-shrink: 0" class="py-2 mx-3 d-none d-sm-block">
                        {{ $t("@tasks:issue.task_states." + task.state) }}
                    </div>
                    <div class="border-right align-self-stretch mx-3 d-none d-sm-block"></div>
                    <div class="flex-grow-1 c-pointer d-inline-block d-sm-block" @click="expandTask(task)">
                        <div class="text-primary font-weight-bold fs-sm-1-3 font-slab">
                            {{ customTaskTitle(task) ?? $t(task.name!, task.nameProps) }}
                        </div>
                        <div v-if="$t(task.name + '_subtitle', task.nameProps) !== task.name + '_subtitle'">
                            {{ $t(task.name + "_subtitle", task.nameProps) }}
                        </div>
                    </div>
                    <div v-if="task.hasAttachments">
                        <i class="fa fa-fw fa-paperclip"></i>
                    </div>
                    <div class="border-right align-self-stretch mx-3"></div>
                    <div style="margin-left: 65px" class="ml-sm-0">
                        <template v-if="task.softDeadline">bis {{ $date(task.softDeadline) }}</template>
                        <template v-else>
                            {{ $date(task.createdAt) }}
                        </template>
                    </div>
                    <div class="ml-4" @click="toggleTaskAccessLogs(task.id!)" v-if="isAdmin" style="cursor: pointer">
                        <i class="fa fa-fw fa-user-clock" :class="{ 'text-muted': task.accessLogs?.length === 0 }"></i>
                    </div>
                </div>
                <AnimateIf :if="isAdmin && !!task.id && showAccessLogs[task.id]">
                    <div class="p-5 bg-light">
                        <h6>{{ trans("task_access_logs.title") }}</h6>
                        <div v-if="!task.accessLogs || task.accessLogs.length === 0">
                            <em class="text-muted">{{ trans("task_access_logs.no_results") }}</em>
                        </div>
                        <div v-else>
                            <table class="table table-hover w-auto">
                                <thead>
                                    <tr>
                                        <th scope="col">{{ trans("task_access_logs.table.date") }}</th>
                                        <th scope="col">{{ trans("task_access_logs.table.user") }}</th>
                                        <th scope="col">{{ trans("task_access_logs.table.app") }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="log in task.accessLogs" :key="log.id">
                                        <td>{{ $datetime(log.accessedAt) }}</td>
                                        <td>{{ log.login?.username || log.login?.email || "Unknown User" }}</td>
                                        <td>{{ log.varsityApp }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </AnimateIf>
                <AnimateIf :if="taskIsExpanded(task)">
                    <div style="max-width: 100%; overflow-x: auto; box-sizing: border-box; contain: layout">
                        <div class="alert alert-debug" v-if="appState.devMode">
                            <b>Task Type:</b>
                            {{ task.taskType }}
                        </div>

                        <CommunicationMessage
                            v-if="task.taskType === 'CommunicationMessage'"
                            v-bind="{ task, index, issue, viewMode }"
                        ></CommunicationMessage>

                        <AcceptOrganizationRequest
                            v-if="task.taskType === 'AcceptOrganizationRequest'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></AcceptOrganizationRequest>

                        <AuditPersonUpload
                            v-if="task.taskType === 'AuditAthleteUpload'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></AuditPersonUpload>

                        <AccountInvitation
                            v-if="task.taskType === 'AccountInvitation'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></AccountInvitation>

                        <JobActivityInvitation
                            v-if="task.taskType === 'JobActivityInvitation'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></JobActivityInvitation>

                        <StaffSignContract
                            v-if="task.taskType === 'StaffSignContract'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></StaffSignContract>

                        <StaffReviewContract
                            v-if="task.taskType === 'StaffReviewContract'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></StaffReviewContract>

                        <StaffReviewGeneralTerms
                            v-if="task.taskType === 'StaffReviewGeneralTerms'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></StaffReviewGeneralTerms>

                        <ApproveOrganizationDebitorProfile
                            v-if="task.taskType === 'ApproveOrganizationDebitorProfile'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ApproveOrganizationDebitorProfile>

                        <!-- This is the old, deprecated version of the task -->
                        <ChampionshipRegistrationTask
                            v-if="task.taskType === 'ChampionshipRegistrationStep'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ChampionshipRegistrationTask>

                        <ChampionshipRegistrationAcceptEventInfo
                            v-if="task.taskType === 'ChampionshipRegistrationAcceptEventInfo'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ChampionshipRegistrationAcceptEventInfo>

                        <ChampionshipRegistrationSelectTeam
                            v-if="task.taskType === 'ChampionshipRegistrationSelectTeam'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ChampionshipRegistrationSelectTeam>

                        <ChampionshipRegistrationApprove
                            v-if="task.taskType === 'ChampionshipRegistrationApprove'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ChampionshipRegistrationApprove>

                        <ChampionshipRegistrationWaitingList
                            v-if="task.taskType === 'ChampionshipRegistrationWaitingList'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ChampionshipRegistrationWaitingList>

                        <ChampionshipRegistrationPayOrder
                            v-if="task.taskType === 'ChampionshipRegistrationPayOrder'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ChampionshipRegistrationPayOrder>

                        <ChampionshipRegistrationFinalizeTeams
                            v-if="task.taskType === 'ChampionshipRegistrationFinalizeTeams'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ChampionshipRegistrationFinalizeTeams>

                        <ChampionshipRegistrationFinalDocuments
                            v-if="task.taskType === 'ChampionshipRegistrationFinalDocuments'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ChampionshipRegistrationFinalDocuments>

                        <ChampionshipRegistrationTeamCancellation
                            v-if="task.taskType === 'ChampionshipRegistrationTeamCancellation'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ChampionshipRegistrationTeamCancellation>

                        <ChampionshipRegistrationAcceptBid
                            v-if="task.taskType === 'ChampionshipRegistrationAcceptBid'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ChampionshipRegistrationAcceptBid>

                        <ChampionshipRegistrationCustom
                            v-if="task.taskType === 'ChampionshipRegistrationCustom'"
                            v-bind="{ task, issue, index, viewMode }"
                        ></ChampionshipRegistrationCustom>

                        <ApproveAddressUpdate
                            v-if="task.taskType === 'ApproveAddressUpdate'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ApproveAddressUpdate>

                        <ApproveLoginJobActivity
                            v-if="task.taskType === 'ApproveLoginJobActivity'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ApproveLoginJobActivity>

                        <ShopSelectProducts
                            v-if="task.taskType === 'ShopSelectProducts'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ShopSelectProducts>

                        <ShopOrderApproval
                            v-if="task.taskType === 'ShopOrderApproval'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ShopOrderApproval>

                        <ShopPayOrder
                            v-if="task.taskType === 'ShopPayOrder'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ShopPayOrder>

                        <ShopOrderConfirmation
                            v-if="task.taskType === 'ShopOrderConfirmation'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></ShopOrderConfirmation>

                        <RefundFailed
                            v-if="task.taskType === 'RefundFailed' && viewMode === 'admin'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></RefundFailed>

                        <SubmitPreparedOrganizationMember
                            v-if="task.taskType === 'SubmitPreparedOrganizationMember'"
                            v-bind="{ task, issue, index, viewMode }"
                            @updated="updated"
                        ></SubmitPreparedOrganizationMember>

                        <!--
Add new task types here - remember to give the component the same name as the task type
-->

                        <TaskAttachmentList
                            :task="task"
                            v-if="(task.attachments?.length ?? 0) > 0"
                        ></TaskAttachmentList>
                    </div>
                </AnimateIf>
            </AnimateIf>
        </div>

        <div v-animate-show="!openReply">
            <div class="p-3 d-flex justify-content-between">
                <div>
                    <VarsityInlineConfirmButton
                        class="btn btn-primary mb-0"
                        :controller="inlineCloseAll"
                        v-if="isAdmin"
                        :disabled="!canAnyTaskBeClosed()"
                    >
                        {{ $t("@tasks:issue.close_all") }}
                    </VarsityInlineConfirmButton>
                </div>
                <div class="d-flex">
                    <div id="taskButtons" class="d-inline-block"></div>
                    <button type="button" class="btn btn-primary" @click="openReply = !openReply">
                        {{ $t("@tasks:issue.reply") }}
                    </button>
                </div>
            </div>
            <VarsityInlineConfirm :controller="inlineCloseAll">
                <template #confirmation>
                    <template v-if="allTasksCanBeClosed()">
                        {{ $t("@tasks:issue.close_all_confirm_all_possible") }}
                    </template>
                    <template v-else>
                        {{ $t("@tasks:issue.close_all_confirm_not_all_possible") }}
                    </template>
                </template>
                <template #success>
                    {{ $t("@tasks:issue.close_all_success") }}
                </template>
            </VarsityInlineConfirm>
            <div id="taskConfirmation"></div>
        </div>

        <AnimateIf :if="openReply">
            <IssueRespond
                v-bind="{ issue, viewMode }"
                @close="openReply = false"
                @responded=";[updated(), (openReply = false)]"
            />
        </AnimateIf>
    </div>
</template>

<style scoped>
.bg-future {
    background-color: #e4e2e5;
}
</style>
